import { BeneficiaryCard } from '@/design-system/beneficiary-card/beneficiary-card';
import { BeneficiaryView } from '@/types/entity-types';
import { cn } from '@/utils/cn';
import { EmployeeStatus } from '@seapoint/server/enums';
import { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { FormSelectField } from '../../../../../../design-system/form-select-field/form-select-field';
import { FormTextField } from '../../../../../../design-system/form-text-field/form-text-field';
import { Inline } from '../../../../../../design-system/inline/inline';
import { Select } from '../../../../../../design-system/select/select';
import { Stack } from '../../../../../../design-system/stack/stack';

type PersonalInformationProps = {
  beneficiaries?: BeneficiaryView[];
  onSelectBeneficiary?: (beneficiary: BeneficiaryView) => void;
};

export const PersonalInformation = ({ beneficiaries = [], onSelectBeneficiary }: PersonalInformationProps) => {
  const { watch } = useFormContext();
  const firstName = watch('firstName') as string;
  const [isFirstNameFocused, setIsFirstNameFocused] = useState(false);

  const filteredBeneficiaries =
    firstName?.trim().length > 0
      ? beneficiaries.filter((beneficiary) => beneficiary.name.toLowerCase().includes(firstName.toLowerCase()))
      : [];

  const handleClickBeneficiary = (beneficiary: BeneficiaryView) => {
    onSelectBeneficiary?.(beneficiary);
  };

  const isBeneficiaryListVisible = isFirstNameFocused && filteredBeneficiaries.length > 0;

  return (
    <Stack space={6} className="w-full">
      <Stack space={4} className="w-full">
        <h2 className="text-lg font-semibold text-muted-foreground">Personal Information</h2>

        <Inline space={4} className="w-full justify-between" wrap="nowrap" align="start">
          <div className="relative w-1/2">
            <FormTextField
              label="First Name"
              inputName="firstName"
              placeholder="Enter first name"
              onFocus={() => setIsFirstNameFocused(true)}
              onBlur={() => {
                setTimeout(() => {
                  setIsFirstNameFocused(false);
                }, 100);
              }}
            />
            {isBeneficiaryListVisible && (
              <div className="absolute z-1 mt-2">
                <Stack className="overflow-hidden rounded-lg shadow-lg">
                  {filteredBeneficiaries.map((beneficiary) => (
                    <div
                      key={beneficiary.id}
                      onClick={() => handleClickBeneficiary(beneficiary)}
                      className={cn('bg-white even:bg-gray-50')}
                    >
                      <BeneficiaryCard
                        key={beneficiary.id}
                        name={beneficiary.name}
                        currency={beneficiary.destination.currency}
                        className={cn('rounded-none border-none bg-transparent hover:bg-amber-100/75')}
                      />
                    </div>
                  ))}
                </Stack>
              </div>
            )}
          </div>
          <div className="w-1/2">
            <FormTextField label="Last Name" inputName="lastName" placeholder="Enter last name" />
          </div>
        </Inline>

        <FormSelectField label="Status" inputName="status" placeholder="Select status" triggerClassName="text-base">
          {Object.values(EmployeeStatus).map((status) => (
            <Select.Item key={status} value={status} className="py-3">
              <p className="text-base font-medium text-black">{status}</p>
            </Select.Item>
          ))}
        </FormSelectField>
      </Stack>
    </Stack>
  );
};
